import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound, AlertCircle } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useThemeStore } from '../../stores/theme.store';

export interface PinRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (pin: string) => void;
  errorMessage?: string;
  remainingAttempts?: number;
  isLoading?: boolean;
  /** Optional override of the title. Defaults to "PIN de Transação". */
  title?: string;
}

const PIN_LENGTH = 6;

export function PinRequiredModal({
  isOpen,
  onClose,
  onSubmit,
  errorMessage,
  remainingAttempts,
  isLoading = false,
  title = 'PIN de Transação',
}: PinRequiredModalProps) {
  const { isDarkMode } = useThemeStore();
  const [digits, setDigits] = useState<string[]>(() =>
    Array.from({ length: PIN_LENGTH }, () => ''),
  );
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  // Reset digits and focus first input whenever the modal is (re)opened
  useEffect(() => {
    if (isOpen) {
      setDigits(Array.from({ length: PIN_LENGTH }, () => ''));
      // Focus the first input after the modal animates in
      const timer = setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Clear entered digits when an error message appears so the user can retry
  useEffect(() => {
    if (errorMessage && isOpen) {
      setDigits(Array.from({ length: PIN_LENGTH }, () => ''));
      const timer = setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, isOpen]);

  const pin = digits.join('');
  const canSubmit = pin.length === PIN_LENGTH && !isLoading;

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < PIN_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      setDigits(next);
      inputsRef.current[index - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowRight' && index < PIN_LENGTH - 1) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
      return;
    }
    if (e.key === 'Enter' && canSubmit) {
      e.preventDefault();
      onSubmit(pin);
    }
  };

  const handlePaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    e.preventDefault();
    const next = [...digits];
    for (let i = 0; i < pasted.length && index + i < PIN_LENGTH; i++) {
      next[index + i] = pasted[i];
    }
    setDigits(next);
    const nextFocus = Math.min(index + pasted.length, PIN_LENGTH - 1);
    inputsRef.current[nextFocus]?.focus();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(pin);
  };

  const titleClass = isDarkMode ? 'text-surface-100' : 'text-gray-900';
  const subtitleClass = isDarkMode ? 'text-surface-400' : 'text-gray-600';
  const inputBase = isDarkMode
    ? 'bg-surface-800 border-surface-700 text-surface-100 focus:border-primary-500'
    : 'bg-white border-gray-300 text-gray-900 focus:border-primary-500';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary-500/10 text-primary-400">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <p className={`text-sm ${titleClass}`}>
              Digite seu PIN de 6 dígitos para autorizar a operação.
            </p>
            <p className={`text-xs mt-1 ${subtitleClass}`}>
              Não compartilhe seu PIN com ninguém.
            </p>
          </div>
        </div>

        {/* PIN inputs */}
        <div className="flex items-center justify-center gap-2">
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputsRef.current[index] = el;
              }}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => handlePaste(index, e)}
              disabled={isLoading}
              aria-label={`Dígito ${index + 1} do PIN`}
              className={`w-11 h-12 rounded-lg border text-center text-xl font-semibold outline-none transition-colors ${inputBase} disabled:opacity-60`}
            />
          ))}
        </div>

        {errorMessage && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{errorMessage}</p>
              {typeof remainingAttempts === 'number' &&
                remainingAttempts > 0 && (
                  <p className="text-xs mt-0.5 opacity-90">
                    Tentativas restantes: {remainingAttempts}
                  </p>
                )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-2">
          <Link
            to="/banking/settings/security"
            className="text-sm text-primary-400 hover:text-primary-300 underline-offset-4 hover:underline"
            onClick={onClose}
          >
            Esqueceu seu PIN?
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={!canSubmit}
              isLoading={isLoading}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default PinRequiredModal;
